import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Plus, FileText, ChevronDown, ChevronUp, Download, Eye, Pencil, Trash2, Check, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  useProformas,
  useCreateProforma,
  useProforma,
  useGenerateProformaPdf,
  useTransitionProforma,
  useDeleteProforma,
  type ProformaListDTO,
} from '../../hooks/useProformas';
import { useProjects } from '../../hooks/useProjects';
import { useClients } from '../../hooks/useClients';
import { useAuthStore } from '../../stores/auth-store';
import { createProformaSchema, type CreateProformaInput } from '@shared/validation/proforma-schemas';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { StatusBadge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Select } from '../../components/ui/Select';
import { Input } from '../../components/ui/Input';
import { SkeletonTable } from '../../components/ui/Skeleton';

type EstadoTab = 'todos' | 'borrador' | 'enviada' | 'enviado_al_cliente' | 'aprobada' | 'rechazada';

const ESTADO_LABELS: Record<EstadoTab, string> = {
  todos: 'Todos',
  borrador: 'Borrador',
  enviada: 'Enviada',
  enviado_al_cliente: 'Enviado al cliente',
  aprobada: 'Aprobada',
  rechazada: 'Rechazada',
};

const MONTH_OPTIONS = [
  { value: '', label: 'Todos los meses' },
  { value: '1', label: 'Enero' },
  { value: '2', label: 'Febrero' },
  { value: '3', label: 'Marzo' },
  { value: '4', label: 'Abril' },
  { value: '5', label: 'Mayo' },
  { value: '6', label: 'Junio' },
  { value: '7', label: 'Julio' },
  { value: '8', label: 'Agosto' },
  { value: '9', label: 'Septiembre' },
  { value: '10', label: 'Octubre' },
  { value: '11', label: 'Noviembre' },
  { value: '12', label: 'Diciembre' },
];

function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getYearOptions(): { value: string; label: string }[] {
  const currentYear = new Date().getFullYear();
  const options = [{ value: '', label: 'Todos los años' }];
  for (let y = currentYear; y >= currentYear - 5; y--) {
    options.push({ value: String(y), label: String(y) });
  }
  return options;
}

export function ProformaListPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const canCreate = ['administrativo', 'gerente', 'operaciones'].includes(user?.role ?? '');

  const [activeTab, setActiveTab] = useState<EstadoTab>('todos');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debounceTimer, setDebounceTimer] = useState<ReturnType<typeof setTimeout>>();
  const [page, setPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters
  const [yearFilter, setYearFilter] = useState('');
  const [monthFilter, setMonthFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');

  // Load clients for filter dropdown
  const { data: clientsData } = useClients({ limit: 100 });
  const clientOptions = useMemo(
    () => [
      { value: '', label: 'Todos los clientes' },
      ...(clientsData?.data ?? []).map((c) => ({ value: c.id, label: c.nombre })),
    ],
    [clientsData],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value);
      if (debounceTimer) clearTimeout(debounceTimer);
      const timer = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 300);
      setDebounceTimer(timer);
    },
    [debounceTimer],
  );

  const filters = useMemo(
    () => ({
      search: debouncedSearch || undefined,
      estado: activeTab === 'todos' ? undefined : activeTab,
      clientId: clientFilter || undefined,
      year: yearFilter ? Number(yearFilter) : undefined,
      month: monthFilter ? Number(monthFilter) : undefined,
      page,
      limit: 20,
    }),
    [debouncedSearch, activeTab, clientFilter, yearFilter, monthFilter, page],
  );

  const { data, isLoading, isError, refetch } = useProformas(filters);
  const tabs: EstadoTab[] = ['todos', 'borrador', 'enviada', 'enviado_al_cliente', 'aprobada', 'rechazada'];

  const handleFilterChange = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setter(e.target.value);
    setPage(1);
  };

  if (isLoading) {
    return (
      <div className="space-y-6 fade-up-1">
        <h1 className="text-2xl font-bold text-brand-dark">Proformas</h1>
        <Card><div className="p-6"><SkeletonTable rows={5} cols={6} /></div></Card>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6 fade-up-1">
        <h1 className="text-2xl font-bold text-brand-dark">Proformas</h1>
        <Card>
          <div className="px-6 py-12 text-center">
            <p className="text-sm text-gray-500">Error al cargar proformas</p>
            <Button variant="secondary" className="mt-4" onClick={() => refetch()}>Reintentar</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-up-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-brand-dark">Proformas</h1>
        {canCreate && (
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => setShowCreateModal(true)}
            className="hidden sm:flex"
          >
            Nueva Proforma
          </Button>
        )}
      </div>

      {/* Search + Filters */}
      <div className="space-y-3">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            className="w-full rounded-lg border border-gray-200 bg-white/80 py-2 pl-9 pr-3 text-sm placeholder:text-gray-400 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            placeholder="Buscar por proyecto, cliente o codigo..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {/* Filter dropdowns */}
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
          <select
            className="rounded-lg border border-gray-200 bg-white/80 px-3 py-2 text-sm text-gray-700 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            value={yearFilter}
            onChange={handleFilterChange(setYearFilter)}
          >
            {getYearOptions().map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="rounded-lg border border-gray-200 bg-white/80 px-3 py-2 text-sm text-gray-700 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            value={monthFilter}
            onChange={handleFilterChange(setMonthFilter)}
          >
            {MONTH_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="rounded-lg border border-gray-200 bg-white/80 px-3 py-2 text-sm text-gray-700 focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue/30"
            value={clientFilter}
            onChange={handleFilterChange(setClientFilter)}
          >
            {clientOptions.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab}
            className={`whitespace-nowrap rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
              activeTab === tab ? 'bg-brand-blue text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
            onClick={() => { setActiveTab(tab); setPage(1); }}
          >
            {ESTADO_LABELS[tab]}
          </button>
        ))}
      </div>

      {/* Content */}
      {data && data.data.length === 0 && !debouncedSearch && !clientFilter && !yearFilter && !monthFilter ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <FileText size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">No hay proformas registradas</h2>
            <p className="mt-1 text-sm text-gray-400">Las proformas aparecen aqui al crearse desde cualquier proyecto.</p>
            {canCreate && (
              <Button variant="primary" className="mt-4" leftIcon={<Plus size={16} />} onClick={() => setShowCreateModal(true)}>
                Crear Proforma
              </Button>
            )}
          </div>
        </Card>
      ) : data && data.data.length === 0 ? (
        <Card>
          <div className="px-6 py-16 text-center">
            <Search size={48} className="mx-auto text-gray-300" />
            <h2 className="mt-4 text-lg font-semibold text-gray-600">Sin resultados</h2>
            <p className="mt-1 text-sm text-gray-400">
              No encontramos proformas con los filtros aplicados.
            </p>
          </div>
        </Card>
      ) : (
        <>
          {/* Desktop Table */}
          <Card className="hidden sm:block">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Codigo PRF</th>
                    <th className="px-4 py-3">Proyecto</th>
                    <th className="px-4 py-3">Cliente</th>
                    <th className="px-4 py-3">Fecha</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3 text-right">Total</th>
                    <th className="px-4 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.data.map((proforma) => (
                    <ProformaRowWithDetail
                      key={proforma.id}
                      proforma={proforma}
                      isExpanded={expandedId === proforma.id}
                      onToggle={() => setExpandedId(expandedId === proforma.id ? null : proforma.id)}
                      onNavigate={(path) => navigate(path)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Mobile Cards */}
          <div className="space-y-3 sm:hidden">
            {data?.data.map((proforma) => (
              <ProformaCard
                key={proforma.id}
                proforma={proforma}
                onClick={() => navigate(`/proformas/${proforma.id}`)}
                onNavigate={(path) => navigate(path)}
              />
            ))}
          </div>

          {/* Pagination */}
          {data && data.pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
              <span className="text-sm text-gray-500">Pagina {page} de {data.pagination.totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= data.pagination.totalPages} onClick={() => setPage((p) => p + 1)}>Siguiente</Button>
            </div>
          )}
        </>
      )}

      {/* Mobile FAB */}
      {canCreate && (
        <button
          className="fixed bottom-6 right-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#dd5113] text-white shadow-lg transition-transform hover:scale-105 sm:hidden"
          onClick={() => setShowCreateModal(true)}
          aria-label="Nueva Proforma"
        >
          <Plus size={24} />
        </button>
      )}

      <CreateProformaModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreated={(id) => navigate(`/proformas/${id}`)}
      />

    </div>
  );
}

// ── Desktop table row + expandable detail ──────────────────────────

function ProformaRowWithDetail({
  proforma,
  isExpanded,
  onToggle,
  onNavigate,
}: {
  proforma: ProformaListDTO;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: (path: string) => void;
}) {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const deleteMutation = useDeleteProforma();
  const approveMutation = useTransitionProforma();
  return (
    <>
      <tr className="border-b border-gray-50 transition-colors hover:bg-gray-50/50">
        <td className="px-4 py-3 font-medium text-gray-800">
          PRF-{String(proforma.numero).padStart(3, '0')}
        </td>
        <td className="px-4 py-3">
          <div>
            <p className="text-gray-800">{proforma.project_nombre}</p>
            <p className="text-xs text-gray-400">{proforma.project_codigo}</p>
          </div>
        </td>
        <td className="px-4 py-3 text-gray-600">{proforma.client_nombre}</td>
        <td className="px-4 py-3 text-xs text-gray-400">
          {new Date(proforma.created_at).toLocaleDateString('es-EC')}
        </td>
        <td className="px-4 py-3"><StatusBadge entity="proforma" status={proforma.estado} /></td>
        <td className="px-4 py-3 text-right font-medium text-gray-800">{formatCurrency(proforma.totals.total)}</td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1">
            <button
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-brand-blue hover:bg-brand-blue/10"
              onClick={onToggle}
              title="Ver detalle"
            >
              <Eye size={14} />
              Ver
              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
            {proforma.estado !== 'aprobada' && proforma.estado !== 'enviado_al_cliente' && (
              <button
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                onClick={() => onNavigate(`/proformas/${proforma.id}`)}
                title="Editar"
              >
                <Pencil size={14} />
              </button>
            )}
            {proforma.estado === 'enviado_al_cliente' && (
              <>
                <button
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-green-600 hover:bg-green-50"
                  title="Aprobar proforma"
                  onClick={async () => {
                    if (window.confirm('¿Aprobar esta proforma? Se habilitará la creación de la Orden de Trabajo.')) {
                      await approveMutation.mutateAsync({ proformaId: proforma.id, action: 'approve' });
                    }
                  }}
                >
                  <Check size={14} />
                  <span>Aprobar</span>
                </button>
                <button
                  className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                  title="Rechazar proforma"
                  onClick={() => setShowRejectModal(true)}
                >
                  <X size={14} />
                  <span>Rechazar</span>
                </button>
              </>
            )}
            {(proforma.estado === 'borrador' || proforma.estado === 'enviado_al_cliente' || proforma.estado === 'enviada' || proforma.estado === 'aprobada' || proforma.estado === 'rechazada') && (
              <button
                className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-red-500 hover:bg-red-50"
                title="Eliminar proforma"
                onClick={async () => {
                  let msg: string;
                  if (proforma.estado === 'aprobada') {
                    msg = 'Esta proforma está aprobada. Si no existe una Orden de Trabajo, se eliminará y el proyecto regresará a estado "Datos Listos". Si ya hay una OT generada, la eliminación será bloqueada. ¿Continuar?';
                  } else if (proforma.estado === 'enviada') {
                    msg = 'Esta proforma está pendiente de aprobación. Al eliminarla deberá crear una nueva. ¿Continuar?';
                  } else if (proforma.estado === 'enviado_al_cliente') {
                    msg = 'Esta proforma ha sido enviada al cliente. Al eliminarla deberá crear una nueva. ¿Continuar?';
                  } else if (proforma.estado === 'rechazada') {
                    msg = 'Esta proforma fue rechazada. Al eliminarla se borrará permanentemente. ¿Continuar?';
                  } else {
                    msg = '¿Eliminar esta proforma? Esta acción no se puede deshacer.';
                  }
                  if (window.confirm(msg)) {
                    await deleteMutation.mutateAsync(proforma.id);
                  }
                }}
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="bg-gray-50/50 px-4 py-0">
            <ProformaDetailPanel proformaId={proforma.id} estado={proforma.estado} onNavigate={onNavigate} />
          </td>
        </tr>
      )}
      {showRejectModal && (
        <RejectProformaModal proformaId={proforma.id} onClose={() => setShowRejectModal(false)} />
      )}
    </>
  );
}

// ── Inline detail panel ─────────────────────────────────────────────

function ProformaDetailPanel({
  proformaId,
  estado,
  onNavigate,
}: {
  proformaId: string;
  estado: string;
  onNavigate: (path: string) => void;
}) {
  const { data: detail, isLoading } = useProforma(proformaId);
  const generatePdf = useGenerateProformaPdf();

  if (isLoading) {
    return (
      <div className="py-4 text-center text-sm text-gray-400">Cargando detalle...</div>
    );
  }

  if (!detail) {
    return (
      <div className="py-4 text-center text-sm text-gray-400">No se pudo cargar el detalle</div>
    );
  }

  const handleDownloadPdf = async () => {
    const result = await generatePdf.mutateAsync(proformaId);
    window.open(result.download_url, '_blank');
  };

  return (
    <div className="space-y-4 py-4">
      {/* Items table */}
      {detail.items.length > 0 ? (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                <th className="px-3 py-2">Codigo</th>
                <th className="px-3 py-2">Descripcion</th>
                <th className="px-3 py-2">Unidad</th>
                <th className="px-3 py-2 text-right">Cantidad</th>
                <th className="px-3 py-2 text-right">P. Unitario</th>
                <th className="px-3 py-2 text-right">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {detail.items.map((item) => {
                const costBase = item.precio_mo + item.precio_materiales;
                const divisor = 1 - item.porcentaje_utilidad / 100;
                const precioUnitarioRaw = divisor > 0 ? costBase / divisor : costBase;
                const precioUnitario = Math.round(precioUnitarioRaw * 100) / 100;
                const subtotal = precioUnitario * item.cantidad;
                return (
                  <tr key={item.id} className="border-b border-gray-50">
                    <td className="px-3 py-2 font-mono text-gray-600">{item.codigo}</td>
                    <td className="px-3 py-2 text-gray-700">{item.descripcion}</td>
                    <td className="px-3 py-2 text-gray-500">{item.unidad}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{item.cantidad}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(precioUnitario)}</td>
                    <td className="px-3 py-2 text-right font-medium text-gray-800">{formatCurrency(subtotal)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-gray-400">Sin items</p>
      )}

      {/* Financial summary */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1 text-xs text-gray-500">
          <p>Subtotal: <span className="font-medium text-gray-700">{formatCurrency(detail.totals.subtotal_con_utilidad)}</span></p>
          {detail.descuento > 0 && (
            <p>Descuento: <span className="font-medium text-red-600">-{formatCurrency(detail.descuento)}</span></p>
          )}
          <p>IVA ({detail.iva_rate}%): <span className="font-medium text-gray-700">{formatCurrency(detail.totals.iva)}</span></p>
          <p className="text-sm font-semibold text-gray-800">Total: {formatCurrency(detail.totals.total)}</p>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          {estado === 'aprobada' && (
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Download size={14} />}
              onClick={handleDownloadPdf}
              isLoading={generatePdf.isPending}
            >
              Descargar PDF
            </Button>
          )}
          {estado !== 'aprobada' && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Pencil size={14} />}
              onClick={() => onNavigate(`/proformas/${proformaId}`)}
            >
              Editar
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate(`/proformas/${proformaId}/preview`)}
          >
            Vista Previa
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Mobile card ─────────────────────────────────────────────────────

function ProformaCard({
  proforma,
  onClick,
  onNavigate,
}: {
  proforma: ProformaListDTO;
  onClick: () => void;
  onNavigate: (path: string) => void;
}) {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const user = useAuthStore((s) => s.user);
  const approveMutation = useTransitionProforma();
  const canApprove = proforma.estado === 'enviado_al_cliente' && ['gerente', 'operaciones', 'administrativo'].includes(user?.role ?? '');

  return (
    <div className="glass-card w-full overflow-hidden">
      <button className="w-full p-4 text-left transition-colors hover:bg-white/70" onClick={onClick}>
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-brand-blue">PRF-{String(proforma.numero).padStart(3, '0')}</p>
            <p className="font-medium text-gray-800">{proforma.project_nombre}</p>
            <p className="text-xs text-gray-400">{proforma.client_nombre}</p>
          </div>
          <p className="font-semibold text-gray-800">{formatCurrency(proforma.totals.total)}</p>
        </div>
        <div className="mt-2 flex items-center gap-2">
          <StatusBadge entity="proforma" status={proforma.estado} />
          <span className="text-xs text-gray-400">{proforma.totals.total_items} items</span>
          <span className="text-xs text-gray-400">{new Date(proforma.created_at).toLocaleDateString('es-EC')}</span>
        </div>
      </button>

      {canApprove && (
        <div className="border-t border-gray-100 px-4 py-3 flex gap-2 flex-wrap">
          <Button
            size="sm"
            className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            isLoading={approveMutation.isPending}
            onClick={async (e) => {
              e.stopPropagation();
              if (window.confirm('¿Aprobar esta proforma? Se habilitará la creación de la Orden de Trabajo.')) {
                await approveMutation.mutateAsync({ proformaId: proforma.id, action: 'approve' });
              }
            }}
          >
            <Check size={14} className="mr-1" />
            Aprobar
          </Button>
          <Button
            size="sm"
            className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            onClick={(e) => {
              e.stopPropagation();
              setShowRejectModal(true);
            }}
          >
            <X size={14} className="mr-1" />
            Rechazar
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(`/proformas/${proforma.id}/preview`);
            }}
          >
            Vista Previa
          </Button>
        </div>
      )}
      {showRejectModal && (
        <RejectProformaModal proformaId={proforma.id} onClose={() => setShowRejectModal(false)} />
      )}
    </div>
  );
}

// ── Reject Proforma Modal ───────────────────────────────────────────

function RejectProformaModal({
  proformaId,
  onClose,
}: {
  proformaId: string;
  onClose: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const rejectMutation = useTransitionProforma();

  const handleReject = async () => {
    const trimmed = motivo.trim();
    if (trimmed.length < 5) return;
    await rejectMutation.mutateAsync({ proformaId, action: 'reject', motivoRechazo: trimmed });
    onClose();
  };

  return (
    <Modal isOpen onClose={onClose} title="Rechazar Proforma" size="sm">
      <div className="space-y-4">
        <p className="text-sm text-gray-600">
          Indica el motivo por el cual el cliente rechazó esta proforma.
        </p>
        <textarea
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
          rows={4}
          placeholder="Motivo del rechazo del cliente..."
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          autoFocus
        />
        {motivo.trim().length > 0 && motivo.trim().length < 5 && (
          <p className="text-xs text-red-500">El motivo debe tener al menos 5 caracteres.</p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button
            variant="primary"
            className="bg-red-600 hover:bg-red-700"
            onClick={handleReject}
            disabled={motivo.trim().length < 5 || rejectMutation.isPending}
            isLoading={rejectMutation.isPending}
          >
            Confirmar Rechazo
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Create Proforma Modal ───────────────────────────────────────────

function CreateProformaModal({
  isOpen,
  onClose,
  onCreated,
}: {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const createMutation = useCreateProforma();
  const { data: projectsData } = useProjects({ limit: 500 });

  const projectOptions = useMemo(
    () =>
      (projectsData?.data ?? []).map((p) => ({
        value: p.id,
        label: `${p.codigo} — ${p.nombre}`,
      })),
    [projectsData],
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateProformaInput>({
    resolver: zodResolver(createProformaSchema),
    defaultValues: { descuento: 0, ivaRate: 15 },
  });

  const onSubmit = handleSubmit(async (data) => {
    const result = await createMutation.mutateAsync(data);
    reset();
    onClose();
    onCreated(result.id);
  });

  return (
    <Modal isOpen={isOpen} title="Nueva Proforma" onClose={onClose} size="md">
      <form onSubmit={onSubmit} className="space-y-4">
        <Select
          label="Proyecto"
          {...register('projectId')}
          error={errors.projectId?.message}
          options={projectOptions}
          placeholder="Seleccionar proyecto..."
        />
        <div className="grid grid-cols-2 gap-4">
          <Input
            label="Descuento ($)"
            type="number"
            step="0.01"
            {...register('descuento', { valueAsNumber: true })}
            error={errors.descuento?.message}
            placeholder="0.00"
          />
          <Input
            label="IVA (%)"
            type="number"
            step="0.01"
            {...register('ivaRate', { valueAsNumber: true })}
            error={errors.ivaRate?.message}
            placeholder="15"
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <Button variant="secondary" onClick={onClose} type="button">Cancelar</Button>
          <Button variant="primary" type="submit" isLoading={createMutation.isPending}>Crear Proforma</Button>
        </div>
      </form>
    </Modal>
  );
}

